import React from 'react';
import { useIntl } from '../../../util/reactIntl';
import { formatMoney } from '../../../util/currency';
import { NamedLink } from '../../../components';
import styles from './SectionFeaturedProducts.module.css';

const getListingImageUrl = listing => {
  const images = listing?.images;
  if (!images || images.length === 0) return null;
  const image = images[0];
  return (
    image?.attributes?.variants?.['listing-card']?.url ||
    image?.attributes?.variants?.['scaled-small']?.url ||
    null
  );
};

const ProductCard = ({ listing, intl }) => {
  const { id, attributes } = listing;
  const { title, price, publicData } = attributes;
  const customUnitType = publicData?.unit_type || '';
  const showUnit = customUnitType && customUnitType !== 'fixed' && customUnitType !== 'item';
  const imageUrl = getListingImageUrl(listing);

  const priceFormatted = price ? formatMoney(intl, price) : null;
  const priceLabel = priceFormatted
    ? showUnit
      ? `${priceFormatted} / ${customUnitType}`
      : priceFormatted
    : null;

  return (
    <NamedLink name="ListingPage" params={{ id: id.uuid, slug: encodeURIComponent(title) }} className={styles.productCard}>
      <div className={styles.imageContainer}>
        {imageUrl ? (
          <img src={imageUrl} alt={title} className={styles.productImage} />
        ) : (
          <div className={styles.imagePlaceholder} />
        )}
      </div>
      <div className={styles.productInfo}>
        <h3 className={styles.productName}>{title}</h3>
        {priceLabel && <p className={styles.price}>{priceLabel}</p>}
      </div>
    </NamedLink>
  );
};

const SkeletonCard = () => (
  <div className={styles.productCard}>
    <div className={`${styles.imageContainer} ${styles.skeleton}`} />
    <div className={styles.productInfo}>
      <div className={`${styles.skeletonText} ${styles.skeletonTitle}`} />
      <div className={`${styles.skeletonText} ${styles.skeletonPrice}`} />
    </div>
  </div>
);

/**
 * Featured Products section for the Landing Page.
 * Shows real listings fetched from Sharetribe API (pub_listingType=sell-products).
 */
const FeaturedProducts = ({ listings = [], inProgress = false }) => {
  const intl = useIntl();
  const showSkeletons = inProgress && listings.length === 0;

  return (
    <section className={styles.featuredSection}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>Featured Products</h2>
          <NamedLink
            name="SearchPageWithListingType"
            params={{ listingType: 'products' }}
            className={styles.viewAll}
          >
            View all
          </NamedLink>
        </div>

        <div className={styles.productsGrid}>
          {showSkeletons
            ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
            : listings.map(listing => (
                <ProductCard key={listing.id.uuid} listing={listing} intl={intl} />
              ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
